import {
  CheckIcon,
  DuplicateIcon,
  GlobeAltIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import React, { useState } from 'react';
import { FaFacebook, FaLinkedin, FaReddit, FaXTwitter } from 'react-icons/fa6';
import { toast } from 'sonner';

import axiosInstance from '@/utils/httpClient';

import Modal from './atoms/Modal';
import Toggle from './atoms/Toggle';

export interface OutputSharing {
  share_uuid?: string | null;
  is_public?: boolean;
}

interface ShareOutputModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Preset title shown under the heading (optional)
  presetTitle?: string;
  // Endpoint toggling sharing: /api/share-completion (logged-in outputs) or
  // /api/share-public-completion (anonymous template form outputs)
  apiPath: string;
  // Row-identifying fields merged into the POST body
  // ({ completion_id } or { uuid })
  payload: Record<string, string | number>;
  sharing: OutputSharing;
  onChange: (sharing: OutputSharing) => void;
}

// Share modal for a single AI output. Same shape as ShareTemplateModal, with
// the social row from the older Pattern-A share modals for distribution.
const ShareOutputModal: React.FC<ShareOutputModalProps> = ({
  isOpen,
  onClose,
  presetTitle,
  apiPath,
  payload,
  sharing,
  onChange,
}) => {
  const { t } = useLocale('tools');
  const [isCopied, setIsCopied] = useState(false);

  const shareUrl =
    typeof window !== 'undefined' && sharing.share_uuid
      ? `${window.location.origin}/share-output/${sharing.share_uuid}`
      : '';

  const updateSharing = async (is_public: boolean) => {
    try {
      const response = await axiosInstance.post(apiPath, {
        ...payload,
        is_public,
      });
      onChange(response.data);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error || t('Failed to update sharing'),
      );
    }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
    toast.success(t('Link copied'));
  };

  return (
    <Modal showModal={isOpen} onClose={onClose} className='sm:max-w-lg'>
      <div className='p-6'>
        <h2 className='mb-1 text-xl font-semibold text-gray-900 dark:text-white'>
          {t('Share Output')}
        </h2>
        {presetTitle && (
          <p className='mb-6 text-sm text-gray-500 dark:text-slate-400'>
            {presetTitle}
          </p>
        )}

        <div className={presetTitle ? '' : 'mt-6'}>
          <div className='flex items-start justify-between'>
            <div className='mr-4 flex'>
              <GlobeAltIcon className='mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400' />
              <div>
                <p className='text-sm font-medium text-gray-900 dark:text-slate-200'>
                  {t('Share with anyone')}
                </p>
                <p className='mt-1 text-xs text-gray-500 dark:text-slate-400'>
                  {t(
                    'Anyone with the link can view this output. It may also be featured as an example on Easy-Peasy.AI.',
                  )}
                </p>
              </div>
            </div>
            <Toggle
              initialValue={!!sharing.is_public}
              onChange={(value: boolean) => updateSharing(value)}
            />
          </div>

          {sharing.is_public && shareUrl && (
            <>
              <div className='mt-3 flex items-center'>
                <input
                  type='text'
                  readOnly
                  value={shareUrl}
                  onFocus={(e) => e.target.select()}
                  className='block w-full rounded-l-md border-gray-300 bg-gray-50 text-xs text-gray-600 focus:border-primary-500 focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 dark:text-slate-300'
                />
                <button
                  type='button'
                  onClick={copyLink}
                  className='inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-white/5 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                >
                  {isCopied ? (
                    <CheckIcon className='h-4 w-4 text-green-500' />
                  ) : (
                    <DuplicateIcon className='h-4 w-4' />
                  )}
                  <span className='ml-1'>
                    {isCopied ? t('Copied') : t('Copy')}
                  </span>
                </button>
              </div>

              <div className='mt-6 flex justify-center space-x-8'>
                <div className='flex flex-col items-center'>
                  <a
                    href={`https://www.linkedin.com/shareArticle?url=${encodeURIComponent(
                      shareUrl,
                    )}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <FaLinkedin className='h-8 w-8 text-gray-400 hover:text-[#0077B5]' />
                  </a>
                  <span className='mt-1 text-xs font-semibold'>LinkedIn</span>
                </div>
                <div className='flex flex-col items-center'>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                      shareUrl,
                    )}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <FaFacebook className='h-8 w-8 text-gray-400 hover:text-[#0866FF]' />
                  </a>
                  <span className='mt-1 text-xs font-semibold'>Facebook</span>
                </div>
                <div className='flex flex-col items-center'>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                      shareUrl,
                    )}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <FaXTwitter className='h-8 w-8 text-gray-400 hover:text-[#000000]' />
                  </a>
                  <span className='mt-1 text-xs font-semibold'>X.com</span>
                </div>
                <div className='flex flex-col items-center'>
                  <a
                    href={`https://www.reddit.com/submit?url=${encodeURIComponent(
                      shareUrl,
                    )}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <FaReddit className='h-8 w-8 text-gray-400 hover:text-[#FF4500]' />
                  </a>
                  <span className='mt-1 text-xs font-semibold'>Reddit</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ShareOutputModal;
